import React, { useState, useEffect } from "react";
import WrapperComponent from "../../commonComponents/WrapperComponent";
import { url } from '../../../util/config';
import './CreateSequenceStyles.css'; // <-- Import your CSS file
import DeliveryPreferenceComponent from './DeliveryPreferenceComponent';
import StepperComponent from './StepperComponent';
import { useLocation } from 'react-router-dom';

const daysList = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const CreateSequence = ({ initialSequence: propInitial = null }) => {
  const location = useLocation();
  const initialSequence = propInitial || (location.state && location.state.initialSequence) || null;
  const [sequenceName, setSequenceName] = useState('');
  const [contacts, setContacts] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedContact, setSelectedContact] = useState('');
  const [selectedProfile, setSelectedProfile] = useState('');
  const [showSummary, setShowSummary] = useState(false);

  // Step 2 states
  const [step, setStep] = useState(1);
  const [triggerCondition, setTriggerCondition] = useState('create');
  const [startConditions, setStartConditions] = useState([]);
  const [showStartDropdown, setShowStartDropdown] = useState(false);
  const [stopSuccessConditions, setStopSuccessConditions] = useState([]);
  const [stopFailureConditions, setStopFailureConditions] = useState([]);
  const [showStopSuccessDropdown, setShowStopSuccessDropdown] = useState(false);
  const [showStopFailureDropdown, setShowStopFailureDropdown] = useState(false);

  const [allDays, setAllDays] = useState(true);
  const [selectedDays, setSelectedDays] = useState([...daysList]);
  const [deliveryTimeType, setDeliveryTimeType] = useState('any');
  const [customTimeFrom, setCustomTimeFrom] = useState('');
  const [customTimeTo, setCustomTimeTo] = useState('');
  const [allowOnce, setAllowOnce] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [tags, setTags] = useState([]);
  const [showTagMenuFor, setShowTagMenuFor] = useState(null); // 'start' | 'success' | 'failure' | null
  const [showConditionOverlay, setShowConditionOverlay] = useState(false);
  const [conditionConfig, setConditionConfig] = useState({
    mode: null, // 'start'|'success'|'failure'
    tag: null, // selected tag object
    operator: 'contains',
    value: ''
  });

  const OPERATORS = [
    { key: 'text_in', label: 'Text In', symbol: 'in' },
    { key: 'contains', label: 'Contains', symbol: 'contains' },
    { key: 'equals', label: 'Equal To', symbol: '=' },
    { key: 'gt', label: 'Greater Than', symbol: '>' },
    { key: 'lt', label: 'Less Than', symbol: '<' },
    { key: 'not_contains', label: 'Not Contains', symbol: 'not contains' }
  ];

  const isEditMode = Boolean(initialSequence && (initialSequence._id || initialSequence.id));

  const [showStepper, setShowStepper] = useState(false);
    const [stepperList, setStepperList] = useState([
    { sendAfter: 1, sendUnit: "Days", showDeliveryPref: false, deliveryPref: {} }
    ]);

  // Fetch contacts for dropdown
  useEffect(() => {
    const fetchContacts = async () => {
      const apiUrl = url + '/percontacts';
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': 'Bearer ' + token
          }
        });
        if (!response.ok) throw new Error('Failed to fetch contacts');
        const data = await response.json();
        setContacts(data || []);
      } catch {
        setContacts([]);
      }
    };
    fetchContacts();
  }, []);

  // fetch tags once
  useEffect(() => {
    let mounted = true;
    const fetchTags = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${url}/tags`, { headers: { Authorization: 'Bearer ' + token } });
        const data = await res.json();
        if (!mounted) return;
        const list = Array.isArray(data) ? data : (data.data || []);
        setTags(list.map(t => ({ id: t._id || t.id, tag_name: t.tag_name || t.name || t.tagName, createdAt: t.createdAt || t.created_at })));
      } catch (e) {
        setTags([]);
      }
    };
    fetchTags();
    return () => { mounted = false; };
  }, []);

  // Fetch profiles for dropdown
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const apiUrl = url + '/profiles';
        const token = localStorage.getItem('token');
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': 'Bearer ' + token
          }
        });
        if (!response.ok) throw new Error('Failed to fetch profiles');
        const data = await response.json();
        setProfiles(data || []);
      } catch {
        setProfiles([]);
      }
    };
    fetchProfiles();
  }, []);

  useEffect(() => {
    if (!initialSequence) return;

    // basic fields
    setSequenceName(initialSequence.name || '');
    setSelectedContact(initialSequence.contactid || '');
    setSelectedProfile(initialSequence.sender || initialSequence.sendFrom || '');
    setTriggerCondition(initialSequence.triggerCondition || 'create');

    // conditions (already in desired object shape)
    setStartConditions(Array.isArray(initialSequence.startConditions) ? initialSequence.startConditions : []);
    setStopSuccessConditions(Array.isArray(initialSequence.stopSuccessConditions) ? initialSequence.stopSuccessConditions : []);
    setStopFailureConditions(Array.isArray(initialSequence.stopFailureConditions) ? initialSequence.stopFailureConditions : []);

    // default delivery preference (array or object)
    const ddRaw = initialSequence.defaultDeliveryPreference;
    const dd = Array.isArray(ddRaw) ? (ddRaw[0] || null) : (ddRaw || null);
    if (dd) {
      setAllDays(!!dd.allDays);
      setSelectedDays(dd.selectedDays && dd.selectedDays.length ? dd.selectedDays : [...daysList]);
      setDeliveryTimeType(dd.deliveryTimeType || 'any');
      setCustomTimeFrom(dd.customTimeFrom || '');
      setCustomTimeTo(dd.customTimeTo || '');
      setAllowOnce(!!dd.allowOnce);
    }

    // steps -> stepperList mapping
    /* if (Array.isArray(initialSequence.steps) && initialSequence.steps.length > 0) {
      const mapped = initialSequence.steps.map(s => ({
        sendAfter: s.sendAfter || 1,
        sendUnit: s.sendUnit || 'Days',
        showDeliveryPref: !!s.showDeliveryPref,
        deliveryPref: s.deliveryPref || {},
        templateName: s.templateDetails?.template?.template_name || s.templateName || null,
        templateDetails: s.templateDetails || null,
        templateDetailsFull: s.templateDetails || null
      }));
      setStepperList(mapped);
      setShowStepper(true);
    } */

    if (Array.isArray(initialSequence.steps) && initialSequence.steps.length > 0) {
      const mapped = initialSequence.steps.map(s => {
        const templateDetails = s.templateDetails || null;

        const flattenedTemplateDetailsFull = templateDetails?.template
          ? {
              ...templateDetails.template,
              template_id: templateDetails.template_id,
              bindings: templateDetails.bindings
            }
          : null;

        return {
          sendAfter: s.sendAfter || 1,
          sendUnit: s.sendUnit || 'Days',
          showDeliveryPref: !!s.showDeliveryPref,
          deliveryPref: s.deliveryPref || {},
          templateName:
            templateDetails?.template?.template_name || s.templateName || null,
          templateDetails: templateDetails,
          templateDetailsFull: flattenedTemplateDetailsFull
        };
      });
      setStepperList(mapped);
      setShowStepper(true);
    }


    // put UI on step 2 and show summary
    setStep(isEditMode ? 1 : 2);
    setShowSummary(true);
  }, [initialSequence]);

  // Step 1: Next button handler
  const handleNext = (e) => {
    e.preventDefault();
    setShowSummary(true);
    setStep(2);
  };

  // Step 2: Back button handler
  const handleBack = () => {
    setStep(1);
  };

  // Step 2: Next button handler
  const handleStep2Next = (e) => {
    e.preventDefault();
    setShowSummary(true);
    setShowStepper(true);
    // You can add further steps here
  };

  // Add Start Condition
  const handleAddStartCondition = (value) => {
    if (!startConditions.includes(value)) {
      setStartConditions([...startConditions, value]);
    }
    setShowStartDropdown(false);
  };

  // Add Stop Success Condition
  const handleAddStopSuccessCondition = (value) => {
    if (!stopSuccessConditions.includes(value)) {
      setStopSuccessConditions([...stopSuccessConditions, value]);
    }
    setShowStopSuccessDropdown(false);
  };

  // Add Stop Failure Condition
  const handleAddStopFailureCondition = (value) => {
    if (!stopFailureConditions.includes(value)) {
      setStopFailureConditions([...stopFailureConditions, value]);
    }
    setShowStopFailureDropdown(false);
  };

  // Days selection logic
  const handleDayToggle = (day) => {
    if (allDays) return;
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const handleAllDaysToggle = () => {
    setAllDays(!allDays);
    setSelectedDays(!allDays ? [...daysList] : []);
  };

  const toggleTagMenu = (mode) => {
    setShowTagMenuFor(prev => (prev === mode ? null : mode));
    // close any old overlay
    setShowConditionOverlay(false);
  };

  const handlePickTag = (tag, mode) => {
    setShowTagMenuFor(null);
    setConditionConfig({ mode, tag, operator: 'contains', value: '' });
    setShowConditionOverlay(true);
  };

  // confirm condition & add to proper array
const handleConfirmCondition = () => {
  const { mode, tag, operator, value } = conditionConfig;
  if (!tag) return;
  const condObj = {
    id: tag.id,
    tag_name: tag.tag_name,
    operator,
    operatorLabel: (OPERATORS.find(o => o.key === operator) || {}).label || operator,
    value: (value || '').slice(0,50)
  };

  if (mode === 'start') {
    setStartConditions(prev => [...prev, condObj]);
  } else if (mode === 'success') {
    setStopSuccessConditions(prev => [...prev, condObj]);
  } else if (mode === 'failure') {
    setStopFailureConditions(prev => [...prev, condObj]);
  }

  setShowConditionOverlay(false);
  setConditionConfig({ mode: null, tag: null, operator: 'contains', value: '' });
};

// remove helpers
const removeStartCondition = (idx) => setStartConditions(prev => prev.filter((_,i)=>i!==idx));
const removeStopSuccessCondition = (idx) => setStopSuccessConditions(prev => prev.filter((_,i)=>i!==idx));
const removeStopFailureCondition = (idx) => setStopFailureConditions(prev => prev.filter((_,i)=>i!==idx));


// Stepper data handler
const handleStepperChange = (idx, data) => {
  setStepperList(prev => {
    const updated = [...prev];
    updated[idx] = data;
    return updated;
  });
};

// Add new stepper
const handleAddStepper = () => {
  if (stepperList.length < 4) {
    setStepperList(prev => [
      ...prev,
      { sendAfter: 1, sendUnit: "Days", showDeliveryPref: false, deliveryPref: {} }
    ]);
  }
};

// Build payload for sequence POST
const buildSequencePayload = (statusCode = 1) => {
    const basedOnContact = contacts.find(c => c._id === selectedContact) || {};
    const payload = {
      name: sequenceName,
      contactid: selectedContact || null,
      header: basedOnContact.header || null,
      sender: selectedProfile || null,
      triggerCondition, // 'create' | 'update' | 'hybrid'
      startConditions: startConditions || [],
      stopSuccessConditions: stopSuccessConditions || [],
      stopFailureConditions: stopFailureConditions || [],
      defaultDeliveryPreference: {
        allDays,
        selectedDays,
        deliveryTimeType,
        customTimeFrom,
        customTimeTo,
        allowOnce
      },
      steps: (stepperList || []).map((s, idx) => ({
        stepNumber: idx + 1,
        sendAfter: s.sendAfter,
        sendUnit: s.sendUnit,
        status: false,
        templateDetails: s.templateDetails || null, // full template object including bindings
        showDeliveryPref: !!s.showDeliveryPref,
        deliveryPref: s.deliveryPref || {}
      })),
      statusCode, // 1 = draft, 2 = publish
    };
    return payload;
  };

  // Submit handler for Draft (1) / Publish (2)
  const sequenceSubmit = async (statusCode = 1) => {
    if (submitting) return;
    setSubmitting(true);
    const payload = buildSequencePayload(statusCode);
    const isEdit = !!(initialSequence && (initialSequence._id || initialSequence.id));
    const seqId = initialSequence?._id || initialSequence?.id || null;
    const method = isEdit ? 'PATCH' : 'POST';
    const endpoint = isEdit && seqId ? `${url}/sequence/${seqId}` : `${url}/sequence`;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error('Sequence submit failed:', errText);
        // handle error UI as needed
      } else {
        const data = await res.json();
        console.log(isEdit ? 'Sequence updated:' : 'Sequence saved:', data);
        // optionally redirect / show success toast / set UI state
      }
    } catch (err) {
      console.error('Sequence submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WrapperComponent>
      <div className="container-fluid mt-3">
        <div className="row g-3" style={{ minHeight: '70vh' }}>
          {/* Left Side - 70% */}
          <div className="col-lg-8 col-md-7 col-12">
            <div className="sequence-card p-3">
              {step === 1 && (
                <>
                  <h2 className="mb-3">Create Sequence</h2>
                  <form onSubmit={handleNext}>
                    <div className="mb-2">
                      <label className="form-label">Sequence Name</label>
                      <input
                        type="text"
                        className="form-control"
                        maxLength={50}
                        value={sequenceName}
                        onChange={e => setSequenceName(e.target.value)}
                        required
                      />
                      <small className="text-muted">
                        {sequenceName.length} / 50 characters
                      </small>
                    </div>
                    <div className="mb-2">
                      <label className="form-label">Sequence Based On</label>
                      <select
                        className="form-select"
                        value={selectedContact}
                        onChange={e => setSelectedContact(e.target.value)}
                        required
                      >
                        <option value="">Select Contact</option>
                        {contacts.map((list, index) => (
                          !list.file_def && list.contact_type === 'Personalized' &&
                          <option key={index} value={list._id}>
                            {list.group_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-2">
                      <label className="form-label">Send From</label>
                      <select
                        className="form-select"
                        value={selectedProfile}
                        onChange={e => setSelectedProfile(e.target.value)}
                        required
                      >
                        <option value="">Select Profile</option>
                        {profiles.map((option, index) => (
                          <option key={index} value={option.whatsapp_no}>
                            {option.whatsapp_no}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="d-flex justify-content-end">
                      <button type="submit" className="btn btn-success mt-2">Next</button>
                    </div>
                  </form>
                </>
              )}

              {step === 2 && (
                <div className="sequence-card position-relative">
                  <h2 className="mb-3">Define Trigger</h2>
                  <form onSubmit={handleStep2Next}>
                    <div className="mb-2">
                      <label className="form-label">Set Trigger Condition</label>
                      <div className="trigger-radio-group">
                        <label>
                          <input
                            type="radio"
                            name="trigger"
                            value="create"
                            checked={triggerCondition === 'create'}
                            onChange={() => setTriggerCondition('create')}
                          />{' '}
                          On Create
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="trigger"
                            value="update"
                            checked={triggerCondition === 'update'}
                            onChange={() => setTriggerCondition('update')}
                          />{' '}
                          On Update
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="trigger"
                            value="hybrid"
                            checked={triggerCondition === 'hybrid'}
                            onChange={() => setTriggerCondition('hybrid')}
                          />{' '}
                          Hybrid (Create & Update)
                        </label>
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label" style={{paddingRight: '5px'}}>Start Condition</label>
                      <button
                        type="button"
                        className="add-btn"
                        onClick={() => toggleTagMenu('start')}
                      >
                        + Add
                      </button>

                      {/* inline drill menu */}
                      {showTagMenuFor === 'start' && (
                        <div className="tag-inline-menu">
                          {tags.length === 0 ? (
                            <div className="tag-inline-empty">No tags</div>
                          ) : (
                            tags.map(t => (
                              <div key={t.id} className="tag-inline-item" onClick={() => handlePickTag(t, 'start')}>
                                {t.tag_name}
                              </div>
                            ))
                          )}
                        </div>
                      )}

                      <div style={{ marginTop: 8 }}>
                        {startConditions.map((cond, idx) => (
                          <span key={idx} className="cond-pill me-2">
                            <span className="cond-text">{cond.tag_name} <small className="cond-op">[{cond.operatorLabel}]</small> <em>{cond.value}</em></span>
                            <button type="button" className="cond-remove" onClick={() => removeStartCondition(idx)}>×</button>
                          </span>
                        ))}
                      </div>
                    </div>
                    {/* STOP CONDITIONS - success */}
                    <div className="mb-2">
                      <label className="form-label" style={{paddingRight: '5px'}}>Stop Conditions</label>
                      <button type="button" className="stop-btn" onClick={() => toggleTagMenu('success')}>+ Success</button>
                      {showTagMenuFor === 'success' && (
                        <div className="tag-inline-menu">
                          {tags.map(t => (
                            <div key={t.id} className="tag-inline-item" onClick={() => handlePickTag(t, 'success')}>
                              {t.tag_name}
                            </div>
                          ))}
                        </div>
                      )}

                      <button type="button" className="stop-btn" onClick={() => toggleTagMenu('failure')}>+ Failure</button>
                      {showTagMenuFor === 'failure' && (
                        <div className="tag-inline-menu">
                          {tags.map(t => (
                            <div key={t.id} className="tag-inline-item" onClick={() => handlePickTag(t, 'failure')}>
                              {t.tag_name}
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={{ marginTop: 8 }}>
                        {stopSuccessConditions.map((cond, idx) => (
                          <span key={`suc-${idx}`} className="cond-pill me-2">
                            <span className="cond-text">{cond.tag_name} <small className="cond-op">[{cond.operatorLabel}]</small> <em>{cond.value}</em></span>
                            <button type="button" className="cond-remove" onClick={() => removeStopSuccessCondition(idx)}>×</button>
                          </span>
                        ))}
                        {stopFailureConditions.map((cond, idx) => (
                          <span key={`fail-${idx}`} className="cond-pill cond-warning me-2">
                            <span className="cond-text">{cond.tag_name} <small className="cond-op">[{cond.operatorLabel}]</small> <em>{cond.value}</em></span>
                            <button type="button" className="cond-remove" onClick={() => removeStopFailureCondition(idx)}>×</button>
                          </span>
                        ))}
                      </div>
                    </div>
                    <hr className="divider" />
                    <h5 className="mb-2">Set Delivery Preference</h5>
                    <DeliveryPreferenceComponent
                        allDays={allDays}
                        selectedDays={selectedDays}
                        handleAllDaysToggle={handleAllDaysToggle}
                        handleDayToggle={handleDayToggle}
                        deliveryTimeType={deliveryTimeType}
                        setDeliveryTimeType={setDeliveryTimeType}
                        customTimeFrom={customTimeFrom}
                        setCustomTimeFrom={setCustomTimeFrom}
                        customTimeTo={customTimeTo}
                        setCustomTimeTo={setCustomTimeTo}
                    />
                    <div className="mb-2" style={{ paddingLeft: '20px' }}>
                      <input
                        type="checkbox"
                        checked={allowOnce}
                        onChange={() => setAllowOnce(!allowOnce)}
                        id="allowOnce"
                        className="form-check-input"
                      />
                      <label htmlFor="allowOnce" className="form-check-label ms-2">
                        Allow contacts to enter sequence only once
                      </label>
                    </div>
                    <div className="d-flex justify-content-between">
                      <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>Back</button>
                      {!showStepper && (
                        <button type="submit" className="btn btn-success">Next</button>
                    )}
                    </div>
                  </form>
                    {/* Arrow to StepperComponent */}
                    {showStepper && (
                    <div className="arrow-connector">
                        <span className="arrow">&#8595;</span>
                    </div>
                    )}
                </div>
              )}

              {/* StepperComponent(s) below Step 2 card */}
                {showStepper && (
                <div className="stepper-list">
                    {stepperList.map((stepData, idx) => (
                    <React.Fragment key={idx}>
                        <StepperComponent
                        stepNumber={idx + 1}
                        onAddStep={handleAddStepper}
                        onChange={handleStepperChange}
                        stepData={stepData}
                        disableAdd={stepperList.length >= 4}
                        header={
                            // Pass selected contact's header (string or undefined)
                            contacts.find(c => c._id === selectedContact)?.header || ''
                        }
                        />
                        {idx < stepperList.length - 1 && (
                        <div className="arrow-connector">
                            <span className="arrow">&#8595;</span>
                        </div>
                        )}
                    </React.Fragment>
                    ))}
                </div>
                )}

            </div>
          </div>
          {/* Right Side - 30% */}
          <div className="col-lg-4 col-md-5 col-12">
            <div className="summary-card">
              <h3 className="summary-title">📋 Sequence</h3>
              
              {(showSummary || isEditMode) ? (
                <div className="summary-content">
                  <div className="summary-content">
                  <div className="summary-row">
                    <label>Name:</label>
                    <span>{sequenceName}</span>
                  </div>
                  <div className="summary-row">
                    <label>Based On:</label>
                    <span>
                      {contacts.find(c => c._id === selectedContact)?.group_name || '—'}
                    </span>
                  </div>
                  <div className="summary-row">
                    <label>Send From:</label>
                    <span>
                      {selectedProfile || '—'}
                    </span>
                  </div>
                  <div className="summary-row">
                    <label>Trigger:</label>
                    <span>
                      {triggerCondition === 'create' ? 'On Create' :
                        triggerCondition === 'update' ? 'On Update' : 'Hybrid (Create & Update)'}
                    </span>
                  </div>
                  <div className="summary-row">
                    <label>Start Conditions:</label>
                    <div className="summary-pills">
                      {startConditions.length > 0 ? startConditions.map((c, i) => (
                        <span key={c.id || i} className="summary-pill">
                          <strong className="pill-name">{c.tag_name}</strong>
                          <small className="pill-op">[{c.operatorLabel}]</small>
                          <em className="pill-value">{c.value}</em>
                        </span>
                      )) : <span className="summary-muted">—</span>}
                    </div>
                  </div>
                  <div className="summary-row">
                    <label>Stop Success:</label>
                    <div className="summary-pills">
                      {stopSuccessConditions.length > 0 ? stopSuccessConditions.map((c, i) => (
                        <span key={c.id || i} className="summary-pill">
                          <strong className="pill-name">{c.tag_name}</strong>
                          <small className="pill-op">[{c.operatorLabel}]</small>
                          <em className="pill-value">{c.value}</em>
                        </span>
                      )) : <span className="summary-muted">—</span>}
                    </div>
                  </div>
                  <div className="summary-row">
                    <label>Stop Failure:</label>
                    <div className="summary-pills">
                      {stopFailureConditions.length > 0 ? stopFailureConditions.map((c, i) => (
                        <span key={c.id || i} className="summary-pill summary-pill-warning">
                          <strong className="pill-name">{c.tag_name}</strong>
                          <small className="pill-op">[{c.operatorLabel}]</small>
                          <em className="pill-value">{c.value}</em>
                        </span>
                      )) : <span className="summary-muted">—</span>}
                    </div>
                  </div>
                  <div className="summary-row">
                    <label>Days:</label>
                    <span>
                      {allDays ? 'All Days' : selectedDays.join(', ')}
                    </span>
                  </div>
                  <div className="summary-row">
                    <label>Time:</label>
                    <span>
                      {deliveryTimeType === 'any'
                        ? 'Any Time'
                        : `${customTimeFrom || '--:--'} to ${customTimeTo || '--:--'}`}
                    </span>
                  </div>
                  <div className="summary-row">
                    <label>Allow Once:</label>
                    <span>{allowOnce ? 'Yes' : 'No'}</span>
                  </div>
                        {showStepper && (
                  <div className="stepper-summary-list mt-3">
                    <h5 className="mb-2 text-success">Steps Added</h5>
                    {stepperList.map((step, idx) => (
                      <div key={idx} className="stepper-summary-card mb-2">
                        <div className="d-flex justify-content-between">
                          <span className="fw-bold">Step {idx + 1}</span>
                          <span className="badge bg-primary">{step.templateName}</span>
                        </div>
                        <div>
                          <span>Send After: </span>
                          <span className="fw-bold">{step.sendAfter} {step.sendUnit}</span>
                        </div>
                        {step.showDeliveryPref && (
                          <div>
                            <span className="fw-bold">Custom Delivery:</span>
                            <div>
                              <span>Days: {step.deliveryPref.allDays ? 'All Days' : (step.deliveryPref.selectedDays || []).join(', ')}</span>
                              <br />
                              <span>Time: {step.deliveryPref.deliveryTimeType === 'any'
                                ? 'Any Time'
                                : `${step.deliveryPref.customTimeFrom || '--:--'} to ${step.deliveryPref.customTimeTo || '--:--'}`}</span>
                              <br />
                              <span>Allow Once: {step.deliveryPref.allowOnce ? 'Yes' : 'No'}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={() => sequenceSubmit(1)}
                    disabled={submitting}
                  >
                    {submitting ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-success flex-fill"
                    onClick={() => sequenceSubmit(2)}
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Publish'}
                  </button>
                </div>
                </div>
                </div>
              ) : (
                <div className="summary-empty">
                  <p>No Sequence Created</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {showConditionOverlay && conditionConfig.tag && (
    <div className="condition-overlay">
      <div className="condition-modal">
        <div className="condition-header">
          <strong>{conditionConfig.tag.tag_name}</strong>
          <button className="btn-close" onClick={() => setShowConditionOverlay(false)}>×</button>
        </div>

        <div className="mb-2">
          <label className="form-label">Operator</label>
          <select
            className="form-select"
            value={conditionConfig.operator}
            onChange={e => setConditionConfig(prev => ({ ...prev, operator: e.target.value }))}
          >
            {OPERATORS.map(op => <option key={op.key} value={op.key}>{op.label}</option>)}
          </select>
        </div>

        <div className="mb-2">
          <label className="form-label">Value</label>
          <input
            type="text"
            className="form-control"
            maxLength={50}
            value={conditionConfig.value}
            onChange={e => setConditionConfig(prev => ({ ...prev, value: e.target.value }))}
            placeholder="Enter comparison value (max 50 chars)"
          />
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-outline-secondary" onClick={() => setShowConditionOverlay(false)}>Cancel</button>
          <button className="btn btn-success" onClick={handleConfirmCondition} disabled={!conditionConfig.value.trim()}>OK</button>
        </div>
      </div>
    </div>
  )}
      </div>
    </WrapperComponent>
  );
};

export default CreateSequence;